import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import { PurchaseOrdersFormTyping } from "../../types";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@redux/hooks";
import { useGetCompanyQuery } from "@redux/features/companies/companiesApi";
import BillingAddressInput from "./BillingAddressInput";
import DeliveryAddressInput from "./DeliveryAddressInput";
import ShippingCostInput from "./ShippingCostInput";
import {
    getDefaultOption,
    getRememberedDropdownValue,
    isControlledDropdownValueValid,
} from "@reusables/dropdowns/BaseDropdown/logic";
import { useCopyHydrationContext } from "../../CopyHydrationContext";

const getDeliveryStorageKey = (companyId: number) =>
    `purchase-order.delivery-address.company-${companyId}`;

export default function AddressSection() {
    const { t } = useTranslation("", { keyPrefix: "purchaseOrder.general" });

    const { control, getValues, resetField, setValue } = useFormContext<PurchaseOrdersFormTyping>();
    const { isCopiedDraft } = useCopyHydrationContext();

    const activeCompany = useAppSelector((state) => state.auth.user?.company);
    const mode = useWatch({
        control,
        name: "mode",
    });

    const { data: activeCompanyFull, isLoading: isActiveCompanyFullLoading } = useGetCompanyQuery(
        activeCompany?.id ?? -1,
        {
            skip: !activeCompany,
        }
    );

    const hydratedCompanyIdRef = React.useRef<number | undefined>();

    useEffect(() => {
        if (mode !== "creation" || !activeCompanyFull) {
            hydratedCompanyIdRef.current = activeCompanyFull?.id;
            return;
        }

        if (hydratedCompanyIdRef.current === activeCompanyFull.id) {
            return;
        }

        if (isCopiedDraft) {
            hydratedCompanyIdRef.current = activeCompanyFull.id;
            return;
        }

        // Company billing settings are stable for PO creation, so hydrate them once and then let
        // the delivery dropdown restore a remembered address or fall back to the primary one.
        setValue("billing_address", activeCompanyFull.billing_address, { shouldDirty: false });
        setValue(
            "is_billing_for_delivery",
            activeCompanyFull.billing_address.is_used_for_delivery,
            { shouldDirty: false }
        );

        const currentDeliveryAddress = getValues("delivery_address");

        if (
            isControlledDropdownValueValid(
                currentDeliveryAddress,
                activeCompanyFull.deliveries,
                (delivery) => delivery.id
            )
        ) {
            hydratedCompanyIdRef.current = activeCompanyFull.id;
            return;
        }

        const rememberedDeliveryAddress = getRememberedDropdownValue(
            getDeliveryStorageKey(activeCompanyFull.id),
            activeCompanyFull.deliveries,
            (delivery) => delivery.id
        )?.[0];

        // Remembered delivery addresses are restored by BaseDropdown. This section only applies the
        // company default when nothing valid has already been selected or restored.
        if (rememberedDeliveryAddress) {
            hydratedCompanyIdRef.current = activeCompanyFull.id;
            return;
        }

        const defaultDeliveryAddress = getDefaultOption(activeCompanyFull.deliveries);

        if (defaultDeliveryAddress) {
            setValue("delivery_address", defaultDeliveryAddress, { shouldDirty: false });
        } else {
            resetField("delivery_address");
        }

        hydratedCompanyIdRef.current = activeCompanyFull.id;
    }, [activeCompanyFull, getValues, isCopiedDraft, mode, resetField, setValue]);

    return (
        <>
            <BillingAddressInput isLoading={isActiveCompanyFullLoading} />
            <DeliveryAddressInput
                options={activeCompanyFull?.deliveries ?? []}
                isLoading={isActiveCompanyFullLoading}
                rememberLastValueByKey={
                    activeCompanyFull ? getDeliveryStorageKey(activeCompanyFull.id) : undefined
                }
            />
            <ShippingCostInput />
        </>
    );
}
